/**
 * Affiliates → Payouts: who is owed, what's still clearing, what's been paid,
 * and what stops each payout (no payout method, no tax form). This view
 * never moves money: payouts are sent from SyncSuite admin → Referrals →
 * Payouts (by Wise). "Remind them" puts a move with a ready message on
 * today's list; nothing is sent.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { BellPlus, CircleAlert, CircleCheck, ExternalLink, HandCoins, Info, ListChecks, Wallet } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Avatar, Button, Skeleton, useToast } from "@/components/ui";
import { Card, Empty, Pill, Stat, timeAgo, usd } from "@/features/command/ui";
import type { PayoutRow, Payouts } from "./types";

/** Where payouts are actually made (documentation/admin-surfaces/admin-referrals.md). */
const PAYOUTS_URL = "https://app.syncsuite.co/admin/referrals?tab=payouts";

const money = (n: number) => usd(n, { cents: Math.round(n * 100) % 100 !== 0 });

export function PayoutsTab({ onReminded }: { onReminded: () => void }) {
  const toast = useToast();
  const { pathname, search } = useLocation();
  const [data, setData] = useState<Payouts | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.get<Payouts>("/affiliates/payouts"));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't load payouts");
      setData((d) => d ?? { rows: [], in_program: 0, totals: { due: 0, pending: 0, paid: 0, blocked: 0, blocked_people: 0 } });
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  const remind = async (r: PayoutRow) => {
    setBusy(r.id);
    try {
      const res = await api.post<{ id: string; created: boolean }>(`/affiliates/${r.id}/remind-payout`);
      toast.success(res.created ? `Added to today's moves for ${r.name}` : `${r.name} is already on today's moves`, "Nothing was sent. Open the move to copy the message.");
      setData((d) => d && { ...d, rows: d.rows.map((x) => (x.id === r.id ? { ...x, reminder_id: res.id } : x)) });
      onReminded();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't add the reminder");
    } finally { setBusy(null); }
  };

  const t = data?.totals;
  const status = (r: PayoutRow) => {
    if (r.blockers.length) return <span className="flex flex-wrap gap-1">{r.blockers.map((b) => <Pill key={b} color="rose">{b}</Pill>)}</span>;
    if (r.due + r.pending > 0) return <span className="inline-flex items-center gap-1 text-[13px] text-emerald-300"><CircleCheck className="h-3.5 w-3.5" />Ready to pay</span>;
    return <span className="text-[13px] text-zinc-500">Nothing owed</span>;
  };
  const action = (r: PayoutRow) => {
    if (!r.blockers.length) return null;
    if (r.reminder_id) {
      return (
        <Link to="/affiliates" className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-xs font-medium text-indigo-300 hover:bg-zinc-800/60 hover:text-indigo-200">
          <ListChecks className="h-3.5 w-3.5" />On today's moves
        </Link>
      );
    }
    return (
      <Button size="sm" variant="secondary" loading={busy === r.id} leftIcon={<BellPlus className="h-3.5 w-3.5" />} onClick={() => void remind(r)}>
        Remind them
      </Button>
    );
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-2.5 rounded-xl border border-indigo-400/20 bg-indigo-500/[0.06] px-3.5 py-3 text-sm text-zinc-300">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-indigo-300" />
        <p className="min-w-0">
          Payouts are sent from SyncSuite admin, under Referrals → Payouts (paid by Wise). This page only shows who is owed and what's in the way; it never moves money.{" "}
          <a href={PAYOUTS_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-medium text-indigo-300 hover:text-indigo-200">
            Open payouts <ExternalLink className="h-3 w-3" />
          </a>
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Owed now" icon={HandCoins} tone={t && t.due > 0 ? "brand" : "default"} big={Boolean(t && t.due > 0)} value={t ? money(t.due) : <Skeleton className="h-7 w-16" />} sub="Ready for the next payout" />
        <Stat label="Still clearing" icon={Wallet} value={t ? money(t.pending) : <Skeleton className="h-7 w-16" />} sub="Waiting out the refund window" />
        <Stat label="Paid out" icon={CircleCheck} value={t ? money(t.paid) : <Skeleton className="h-7 w-16" />} sub="Already sent, all time" />
        <Stat
          label="Blocked"
          icon={CircleAlert}
          tone={t && t.blocked > 0 ? "bad" : "default"}
          value={t ? money(t.blocked) : <Skeleton className="h-7 w-16" />}
          sub={t ? (t.blocked_people ? `${t.blocked_people} ${t.blocked_people === 1 ? "person needs" : "people need"} to finish setup` : "Nobody is held up") : ""}
        />
      </div>

      <Card as="section" className="min-w-0 overflow-hidden">
        <div className="flex items-center gap-2 border-b border-zinc-800/70 px-4 py-3">
          <HandCoins className="h-4 w-4 text-indigo-300" />
          <h2 className="text-sm font-semibold text-zinc-100">Who is owed</h2>
          {data && <span className="ml-auto text-[11px] text-zinc-500">{data.in_program} in the program</span>}
        </div>
        {data == null ? (
          <div className="space-y-2 p-4">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-11 w-full rounded-lg" />)}</div>
        ) : data.rows.length === 0 ? (
          <Empty icon={Wallet} title="Nobody has earned anything yet">
            Commission shows up here when a referred customer pays. It clears after the refund window, then it's owed until the next payout.
          </Empty>
        ) : (
          <>
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full min-w-[680px] text-sm">
                <thead>
                  <tr className="border-b border-zinc-800/70 text-left text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                    <th className="px-4 py-2.5 font-semibold">Affiliate</th>
                    <th className="px-2 py-2.5 text-right font-semibold">Owed</th>
                    <th className="px-2 py-2.5 text-right font-semibold">Clearing</th>
                    <th className="px-2 py-2.5 text-right font-semibold">Paid</th>
                    <th className="px-3 py-2.5 font-semibold">What's in the way</th>
                    <th className="px-4 py-2.5" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800/50">
                  {data.rows.map((r) => (
                    <tr key={r.id} className="transition-colors hover:bg-zinc-800/25">
                      <td className="max-w-[220px] px-4 py-2.5">
                        <Link to={`/affiliates/${r.id}`} state={{ from: pathname + search }} className="group flex min-w-0 items-center gap-2.5">
                          <Avatar name={r.name} email={r.email} size="sm" />
                          <span className="min-w-0">
                            <span className="block truncate font-medium text-zinc-100 group-hover:text-indigo-300">{r.name}</span>
                            <span className="block truncate text-[11px] text-zinc-500">{r.last_commission_at ? `Last commission ${timeAgo(r.last_commission_at)}` : r.email}</span>
                          </span>
                        </Link>
                      </td>
                      <td className={cn("px-2 py-2.5 text-right tabular-nums", r.due > 0 ? "font-semibold text-amber-300" : "text-zinc-500")}>{money(r.due)}</td>
                      <td className="px-2 py-2.5 text-right tabular-nums text-zinc-400">{money(r.pending)}</td>
                      <td className="px-2 py-2.5 text-right tabular-nums text-zinc-300">{money(r.paid)}</td>
                      <td className="px-3 py-2.5">{status(r)}</td>
                      <td className="w-px whitespace-nowrap px-4 py-2.5 text-right">{action(r)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="divide-y divide-zinc-800/60 md:hidden">
              {data.rows.map((r) => (
                <div key={r.id} className="space-y-2 px-4 py-3.5">
                  <div className="flex items-center justify-between gap-3">
                    <Link to={`/affiliates/${r.id}`} state={{ from: pathname + search }} className="flex min-w-0 items-center gap-2.5">
                      <Avatar name={r.name} email={r.email} size="sm" />
                      <span className="truncate font-medium text-zinc-100">{r.name}</span>
                    </Link>
                    <span className={cn("shrink-0 text-sm tabular-nums", r.due > 0 ? "font-semibold text-amber-300" : "text-zinc-500")}>{money(r.due)} owed</span>
                  </div>
                  <div className="text-xs text-zinc-500">{money(r.pending)} clearing · {money(r.paid)} paid</div>
                  <div className="flex flex-wrap items-center justify-between gap-2">{status(r)}{action(r)}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
